import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Card, CardContent, Typography, Button } from '@mui/material';
import PhotoLibraryOutlinedIcon from '@mui/icons-material/PhotoLibraryOutlined';
import AddPhotoAlternateOutlinedIcon from '@mui/icons-material/AddPhotoAlternateOutlined';
import { useFeatureFlags } from '../contexts/FeatureFlagContext';

// Mirrors interface from TripDetailPage / TripPhotosPage
interface TripPhoto {
    id: string;
    url: string;
    thumbnail: string;
    caption?: string;
    aiTags?: string[];
    uploadedAt: string;
}

interface TripPhotosSectionProps {
    photos: TripPhoto[];
    tripId: string;
    variant?: 'default' | 'hero';
    onShareClick?: () => void;
    onUploadClick?: () => void;
}

// Heuristic: detect video URL — mirrors TripPhotosPage.tsx exactly for consistency
const isVideoUrl = (url: string): boolean => {
    if (!url) return false;
    const videoExtensions = ['.mp4', '.mov', '.webm', '.ogg', '.avi', '.m4v'];
    const lowerUrl = url.toLowerCase().split('?')[0];
    return videoExtensions.some(ext => lowerUrl.endsWith(ext));
};

const TripPhotosSection: React.FC<TripPhotosSectionProps> = ({
    photos,
    tripId,
    variant = 'default',
    onShareClick,
    onUploadClick,
}) => {
    const navigate = useNavigate();

    // Booking flag — fail-closed (hidden until flag confirms enabled)
    const { isEnabled, isLoading: flagsLoading } = useFeatureFlags();
    const bookingEnabled = !flagsLoading && isEnabled('booking_enabled') === true;
    const isHero = variant === 'hero';
    const maxPreview = isHero ? 5 : 3;
    const previewPhotos = photos.slice(0, maxPreview);
    const remainingCount = Math.max(0, photos.length - maxPreview);

    // Empty state: only render in hero variant
    // (default variant returns null when no photos — no clutter on upcoming/ongoing)
    if (photos.length === 0) {
        if (!isHero) return null;
        return (
            <Card sx={{ borderRadius: 3, mb: 3, p: 3, textAlign: 'center', bgcolor: '#FAFAFA' }}>
                <PhotoLibraryOutlinedIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                <Typography variant="h6" sx={{ mb: 1 }}>
                    Your trip ended — add photos to capture the memory
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Photos help you share your trip and look back on great moments
                </Typography>
                <Button
                    variant="contained"
                    startIcon={<AddPhotoAlternateOutlinedIcon />}
                    onClick={onUploadClick ? onUploadClick : () => navigate(`/trips/${tripId}/photos`)}
                    sx={{ bgcolor: '#2D6E6A', '&:hover': { bgcolor: '#009688' } }}
                >
                    Upload Photos
                </Button>
            </Card>
        );
    }

    // Photos exist — render preview grid
    const tileSize = isHero ? { xs: 100, sm: 140 } : { xs: 80, sm: 100 };
    const gap = isHero ? 1.5 : 1;

    return (
        <Card sx={{ borderRadius: 3, mb: 3 }}>
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                {/* Header row: title + count + actions */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PhotoLibraryOutlinedIcon sx={{ color: '#2D6E6A' }} />
                        <Typography variant={isHero ? 'h5' : 'h6'} sx={{ fontWeight: 600 }}>
                            Photos ({photos.length})
                        </Typography>
                    </Box>

                </Box>

                {/* Thumbnail grid (horizontal scroll on overflow) */}
                <Box
                    sx={{
                        display: 'flex',
                        gap: gap,
                        overflowX: 'auto',
                        pb: 1,
                        // Hide scrollbar visually but keep functional
                        '&::-webkit-scrollbar': { display: 'none' },
                        scrollbarWidth: 'none',
                    }}
                >
                    {previewPhotos.map((photo, idx) => {
                        const isLastTile = idx === previewPhotos.length - 1;
                        const showOverlay = isLastTile && remainingCount > 0;
                        const isVideo = isVideoUrl(photo.url);
                        return (
                            <Box
                                key={photo.id}
                                onClick={() => navigate(`/trips/${tripId}/photos`)}
                                sx={{
                                    position: 'relative',
                                    width: tileSize,
                                    height: tileSize,
                                    flexShrink: 0,
                                    borderRadius: 2,
                                    overflow: 'hidden',
                                    cursor: 'pointer',
                                    bgcolor: '#F0F0F0',
                                    transition: 'transform 0.15s ease',
                                    '&:hover': { transform: 'scale(1.02)' },
                                }}
                            >
                                {isVideo ? (
                                    <Box
                                        component="video"
                                        src={photo.url}
                                        muted
                                        playsInline
                                        preload="metadata"
                                        sx={{
                                            width: '100%',
                                            height: '100%',
                                            objectFit: 'cover',
                                            display: 'block',
                                        }}
                                    />
                                ) : (
                                    <Box
                                        sx={{
                                            width: '100%',
                                            height: '100%',
                                            backgroundImage: `url(${photo.thumbnail || photo.url})`,
                                            backgroundSize: 'cover',
                                            backgroundPosition: 'center',
                                        }}
                                    />
                                )}
                                {/* Video badge if applicable (not shown on +N overlay tile) */}
                                {isVideo && !showOverlay && (
                                    <Box
                                        sx={{
                                            position: 'absolute',
                                            top: 4,
                                            right: 4,
                                            bgcolor: 'rgba(0,0,0,0.6)',
                                            color: 'white',
                                            fontSize: '0.65rem',
                                            px: 0.75,
                                            py: 0.25,
                                            borderRadius: 1,
                                        }}
                                    >
                                        VIDEO
                                    </Box>
                                )}
                                {/* +N more overlay on last tile */}
                                {showOverlay && (
                                    <Box
                                        sx={{
                                            position: 'absolute',
                                            inset: 0,
                                            bgcolor: 'rgba(0,0,0,0.55)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            color: 'white',
                                            fontSize: isHero ? '1.5rem' : '1.1rem',
                                            fontWeight: 600,
                                        }}
                                    >
                                        +{remainingCount}
                                    </Box>
                                )}
                            </Box>
                        );
                    })}
                </Box>

                {/* Footer: View all link + (hero only) Book again CTA */}
                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                    <Button
                        onClick={() => navigate(`/trips/${tripId}/photos`)}
                        sx={{ color: '#2D6E6A', textTransform: 'none', fontWeight: 500 }}
                    >
                        View all {photos.length} photos →
                    </Button>

                    {/* "Book this trip again" CTA — hero variant, only when booking is enabled */}
                    {isHero && bookingEnabled && (
                        <Button
                            variant="outlined"
                            onClick={() => navigate(`/book?from=trip-detail&tripId=${tripId}`)}
                            sx={{
                                borderColor: '#2D6E6A',
                                color: '#2D6E6A',
                                textTransform: 'none',
                                '&:hover': {
                                    borderColor: '#009688',
                                    bgcolor: 'rgba(0, 180, 160, 0.04)',
                                },
                            }}
                        >
                            Book this trip again
                        </Button>
                    )}
                </Box>
            </CardContent>
        </Card>
    );
};

export default TripPhotosSection;
